import { useEffect, useState, type ReactNode } from "react";

interface DeferredMountProps {
  children: ReactNode;
  /** Idle timeout fallback (ms) before mounting if no interaction. */
  idleTimeout?: number;
}

/**
 * Delays rendering its children until the browser is idle or the user
 * interacts with the page (scroll / pointer / keydown / touch).
 *
 * Use this to defer non-critical lazy chunks (toasts, cookie banner,
 * back-to-top, chatbot) so they don't compete with the LCP and reduce
 * unused JS shipped on initial load.
 */
const DeferredMount = ({ children, idleTimeout = 2500 }: DeferredMountProps) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let done = false;
    const mount = () => {
      if (done) return;
      done = true;
      cleanup();
      setReady(true);
    };

    const events: Array<keyof WindowEventMap> = [
      "pointerdown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    events.forEach((e) =>
      window.addEventListener(e, mount, { once: true, passive: true })
    );

    const idle: (cb: () => void) => number =
      (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number })
        .requestIdleCallback
        ? (cb) =>
            (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
              .requestIdleCallback(cb, { timeout: 2000 })
        : (cb) => window.setTimeout(cb, 1);

    const t = window.setTimeout(() => idle(mount), idleTimeout);

    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, mount));
      clearTimeout(t);
    }

    return cleanup;
  }, [ready, idleTimeout]);

  return ready ? <>{children}</> : null;
};

export default DeferredMount;
